import React, { useEffect, useMemo, useRef, useState } from "react";
import "./TagManager.css";

const TAG_THUMBNAIL_BASE_URL =
  import.meta.env.VITE_TAG_THUMBNAIL_BASE_URL ||
  "https://storage.googleapis.com/acz-monke-art-flow-media-files/tags_thumbnails";

function slugifyTagName(value) {
  return (value || "")
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-zA-Z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .toLowerCase() || "tag";
}

function buildTagThumbnailUrl(tooltipName) {
  return `${TAG_THUMBNAIL_BASE_URL}/${tooltipName}-portrait.webp`;
}

function LazyTagImage({ src, alt, className }) {
  const imgRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (!imgRef.current) return;
    if ("loading" in HTMLImageElement.prototype) {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
            observer.disconnect();
          }
        });
      },
      { rootMargin: "200px" }
    );
    observer.observe(imgRef.current);
    return () => observer.disconnect();
  }, [src]);

  return (
    <img
      ref={imgRef}
      src={isVisible ? src : ""}
      data-src={src}
      alt={alt}
      loading="lazy"
      className={className}
    />
  );
}

export default function TagManager({
  prefix,
  tagsContainerClass,
  headingTag,
  guide,
  selectedTags,
  tagLabelById,
  projectTags,
  favoriteTags,
  activeProjectId,
  projectTagsLoading,
  favoriteTagsLoading,
  onAddSelectionToProject,
  onAddSelectionToFavorites,
  onClearProjectTags,
  onClearFavoriteTags,
  onRemoveProjectTag,
  onRemoveFavoriteTag,
  onToggleTag,
}) {
  const [tagSearch, setTagSearch] = useState("");
  const [tagsExpanded, setTagsExpanded] = useState(false);
  const [openMenu, setOpenMenu] = useState(null);
  const groups = guide?.groups || [];
  const tags = guide?.tags || [];
  const containerClass = tagsContainerClass || `${prefix}-tags`;
  const HeadingTag = headingTag || "h4";
  const normalizedSearch = tagSearch.trim().toLowerCase();
  const shouldFilter = normalizedSearch.length >= 2;
  const showTagsList = tagsExpanded || shouldFilter;
  const filteredTags = useMemo(() => {
    if (!shouldFilter) return tags;
    return tags.filter((tag) => {
      const label = tag.label?.fr || tag.label?.en || tag.label || tag.id;
      return String(label).toLowerCase().includes(normalizedSearch);
    });
  }, [tags, shouldFilter, normalizedSearch]);
  const addTagsToSelection = (tagIds) => {
    if (!tagIds.length) return;
    tagIds.forEach((tagId) => {
      if (!selectedTags.has(tagId)) {
        onToggleTag(tagId);
      }
    });
  };

  useEffect(() => {
    if (!openMenu) return undefined;
    const handlePointer = (event) => {
      const target = event.target;
      if (target?.closest?.(".tag-context-menu")) return;
      if (target?.closest?.(".tag-context-trigger")) return;
      setOpenMenu(null);
    };
    document.addEventListener("mousedown", handlePointer);
    return () => document.removeEventListener("mousedown", handlePointer);
  }, [openMenu]);

  return (
    <>
      <div className={`${prefix}-tag-collections`}>
        <div className={`${prefix}-tag-collection`}>
          <div className={`${prefix}-tag-collection-head`}>
            <HeadingTag>Tags du projet</HeadingTag>
            <button
              type="button"
              className={`${prefix}-tag-clear`}
              onClick={onClearProjectTags}
              disabled={!projectTags.length}
              aria-label="Vider"
            >
              <span className="material-symbols-rounded">close</span>
            </button>
          </div>
          <div className={`${prefix}-tag-collection-actions`}>
            <button
              type="button"
              className={`${prefix}-tag-action`}
              onClick={onAddSelectionToProject}
              disabled={!activeProjectId || projectTagsLoading}
            >
              Ajouter selection
            </button>
            {!activeProjectId && <span className={`${prefix}-tag-muted`}>Aucun projet selectionne.</span>}
          </div>
          <div className={`${prefix}-tag-collection-list`}>
            {projectTags.length === 0 && (
              <span className={`${prefix}-tag-muted`}>Aucun tag projet.</span>
            )}
            {projectTags.map((tagId) => (
              <button
                key={tagId}
                type="button"
                className={`${prefix}-tag-item tag-thumbnail-card ${selectedTags.has(tagId) ? "active" : ""}`}
                onClick={() => addTagsToSelection([tagId])}
                aria-pressed={selectedTags.has(tagId)}
              >
                <LazyTagImage
                  src={buildTagThumbnailUrl(slugifyTagName(tagLabelById.get(tagId) || tagId))}
                  alt={tagLabelById.get(tagId) || tagId}
                  className="tag-thumbnail-image"
                />
                <span className="tag-thumbnail-label">{tagLabelById.get(tagId) || tagId}</span>
                <div className="tag-context">
                  <button
                    type="button"
                    className="tag-context-trigger"
                    onClick={(event) => {
                      event.stopPropagation();
                      setOpenMenu((prev) =>
                        prev?.kind === "project" && prev.tagId === tagId ? null : { kind: "project", tagId }
                      );
                    }}
                    aria-label="Menu tag"
                  >
                    <span className="material-symbols-rounded">more_vert</span>
                  </button>
                  {openMenu?.kind === "project" && openMenu.tagId === tagId && (
                    <div className="tag-context-menu" role="menu">
                      <button
                        type="button"
                        className="tag-context-item"
                        onClick={(event) => {
                          event.stopPropagation();
                          onRemoveProjectTag(tagId);
                          setOpenMenu(null);
                        }}
                      >
                        Supprimer du projet
                      </button>
                    </div>
                  )}
                </div>
              </button>
            ))}
          </div>
          <div className={`${prefix}-tag-collection-actions`}>
            <button
              type="button"
              className={`${prefix}-tag-action`}
              onClick={() => addTagsToSelection(projectTags)}
              disabled={!projectTags.length}
            >
              Utiliser
            </button>
          </div>
        </div>
        <div className={`${prefix}-tag-collection`}>
          <div className={`${prefix}-tag-collection-head`}>
            <HeadingTag>Tags favoris</HeadingTag>
            <button
              type="button"
              className={`${prefix}-tag-clear`}
              onClick={onClearFavoriteTags}
              disabled={!favoriteTags.length}
              aria-label="Vider"
            >
              <span className="material-symbols-rounded">close</span>
            </button>
          </div>
          <div className={`${prefix}-tag-collection-actions`}>
            <button
              type="button"
              className={`${prefix}-tag-action`}
              onClick={onAddSelectionToFavorites}
              disabled={favoriteTagsLoading}
            >
              Ajouter selection
            </button>
          </div>
          <div className={`${prefix}-tag-collection-list`}>
            {favoriteTags.length === 0 && (
              <span className={`${prefix}-tag-muted`}>Aucun tag favori.</span>
            )}
            {favoriteTags.map((tagId) => (
              <button
                key={tagId}
                type="button"
                className={`${prefix}-tag-item tag-thumbnail-card ${selectedTags.has(tagId) ? "active" : ""}`}
                onClick={() => onToggleTag(tagId)}
                aria-pressed={selectedTags.has(tagId)}
              >
                <LazyTagImage
                  src={buildTagThumbnailUrl(slugifyTagName(tagLabelById.get(tagId) || tagId))}
                  alt={tagLabelById.get(tagId) || tagId}
                  className="tag-thumbnail-image"
                />
                <span className="tag-thumbnail-label">{tagLabelById.get(tagId) || tagId}</span>
                <div className="tag-context">
                  <button
                    type="button"
                    className="tag-context-trigger"
                    onClick={(event) => {
                      event.stopPropagation();
                      setOpenMenu((prev) =>
                        prev?.kind === "favorite" && prev.tagId === tagId ? null : { kind: "favorite", tagId }
                      );
                    }}
                    aria-label="Menu tag"
                  >
                    <span className="material-symbols-rounded">more_vert</span>
                  </button>
                  {openMenu?.kind === "favorite" && openMenu.tagId === tagId && (
                    <div className="tag-context-menu" role="menu">
                      <button
                        type="button"
                        className="tag-context-item"
                        onClick={(event) => {
                          event.stopPropagation();
                          onRemoveFavoriteTag(tagId);
                          setOpenMenu(null);
                        }}
                      >
                        Supprimer des favoris
                      </button>
                    </div>
                  )}
                </div>
              </button>
            ))}
          </div>
          <div className={`${prefix}-tag-collection-actions`}>
            <button
              type="button"
              className={`${prefix}-tag-action`}
              onClick={() => addTagsToSelection(favoriteTags)}
              disabled={!favoriteTags.length}
            >
              Utiliser
            </button>
          </div>
        </div>
        <div className={`${prefix}-tag-search`}>
          <span className={`${prefix}-tag-search-label`}>Rechercher</span>
          <div className={`${prefix}-tag-search-field`}>
            <input
              type="search"
              placeholder="Filtrer les tags..."
              value={tagSearch}
              onChange={(event) => setTagSearch(event.target.value)}
              aria-label="Rechercher un tag"
            />
            {tagSearch && (
              <button
                type="button"
                className={`${prefix}-tag-search-clear`}
                onClick={() => setTagSearch("")}
                aria-label="Effacer la recherche"
              >
                <span className="material-symbols-rounded">close</span>
              </button>
            )}
          </div>
        </div>
      </div>
      <div className={`${prefix}-tag-expand`}>
        <button
          type="button"
          className={`${prefix}-tag-expand-btn`}
          onClick={() => setTagsExpanded((prev) => !prev)}
          aria-expanded={showTagsList}
        >
          <span className="material-symbols-rounded">
            {showTagsList ? "expand_less" : "expand_more"}
          </span>
          {showTagsList ? "Masquer les tags" : "Afficher les tags"}
        </button>
      </div>
      {showTagsList && (
        <div className={containerClass}>
          {groups.map((group) => (
            <div key={group.id} className={`${prefix}-tag-group`}>
              <h4>{group.emoji ? `${group.emoji} ` : ""}{group.label}</h4>
              <div className={`${prefix}-chip-row`}>
                {filteredTags
                  .filter((tag) => tag.group_id === group.id)
                  .map((tag) => {
                    const tagLabel = tag.label?.fr || tag.label?.en || tag.label || tag.id;
                    const tooltipName = slugifyTagName(tagLabel);
                    return (
                      <button
                        key={tag.id}
                        type="button"
                        className={`${selectedTags.has(tag.id) ? `${prefix}-chip active` : `${prefix}-chip`} tag-thumbnail-card`}
                        onClick={() => onToggleTag(tag.id)}
                        aria-pressed={selectedTags.has(tag.id)}
                      >
                        <LazyTagImage
                          src={buildTagThumbnailUrl(tooltipName)}
                          alt={tagLabel}
                          className="tag-thumbnail-image"
                        />
                        <span className="tag-thumbnail-label">{tagLabel}</span>
                      </button>
                    );
                  })}
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );
}
